/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { StringUnion } from "app/common/StringUnion";
import { ThemePrefs } from "app/common/ThemePrefs";

export const SortPref = StringUnion("name", "date");
export type SortPref = typeof SortPref.type;

export const ViewPref = StringUnion("list", "icons");
export type ViewPref = typeof ViewPref.type;

// A collection of preferences related to a user or org (or combination).
export interface Prefs {
  // A dummy field used only in tests.
  placeholder?: string;
}

// A collection of preferences related to a user.
export interface UserPrefs extends Prefs {
  // Whether to ask the user to fill out a form about their use-case, on opening the DocMenu page.
  // Set to true on first login, then reset when the form is closed, so that it only shows once.
  showNewUserQuestions?: boolean;
  // Whether to record a new sign-up event via Google Tag Manager. Set to true on first login, then
  // reset on first page load (after the event is sent), so that it's only recorded once.
  recordSignUpEvent?: boolean;
  // Theme-related preferences.
  theme?: ThemePrefs;
  // List of deprecated warnings user have seen. Kept for historical reasons as some users have them in their prefs.
  seenDeprecatedWarnings?: DeprecationWarning[];
  // List of dismissedPopups user have seen.
  dismissedPopups?: DismissedPopup[];
  // Behavioral prompt preferences.
  behavioralPrompts?: BehavioralPromptPrefs;
  // Welcome popups a user has dismissed.
  dismissedWelcomePopups?: DismissedReminder[];
  // Localization support.
  locale?: string;
  // If only documents should be shown on the All Documents page.
  onlyShowDocuments?: boolean;
}

// A collection of preferences related to a combination of user and org.
export interface UserOrgPrefs extends Prefs {
  docMenuSort?: SortPref;
  docMenuView?: ViewPref;

  // List of example docs that the user has seen and dismissed the welcome card for.
  // The numbers are the `id` from IExampleInfo in app/client/ui/ExampleInfo.
  // By living in UserOrgPrefs, this applies only to the examples-containing org.
  seenExamples?: number[];

  // Whether the user should see the onboarding tour of Grist. False by default, since existing
  // users should not see it. New users get this set to true when the user is created. This
  // applies to the personal org only; the tour is currently only shown there.
  showGristTour?: boolean;

  // List of document IDs where the user has seen and dismissed the document tour.
  seenDocTours?: string[];

  // Pinned items for non-document resources (bots, collections, agents, flows, maps)
  // Document pinning is handled separately in the docs table
  pinnedItems?: {
    bots?: string[];
    collections?: string[];
    knetAgents?: string[];
    knetFlows?: string[];
    gisProjects?: string[];
  };
}

/** Prefs fields that may never hold a value: keys live in KNet Vault (openspec assistant-key-in-knet-vault). */
export const SECRET_PREF_FIELDS = ["assistantApiKey"] as const;

/** The secret fields `prefs` would store (non-empty strings); an empty value deletes and is allowed. */
export function refusedSecretPrefs(prefs: unknown): string[] {
  if (!prefs || typeof prefs !== "object") { return []; }
  return SECRET_PREF_FIELDS.filter((f) => {
    const v = (prefs as Record<string, unknown>)[f];
    return typeof v === "string" && v.trim() !== "";
  });
}

export const SECRET_PREF_MESSAGE = "Provider keys are kept in KNet Vault (/secrets), in the organization's vault, " +
  "not in org settings.";

export interface OrgPrefs extends Prefs {
  /* The URL (might be data url) of the custom logo to use for the org. */
  customLogoUrl?: string | null;
  /* Legacy: the assistant's key now lives in KNet Vault, org vault `org-<id>` (openspec assistant-key-in-knet-vault).
   * Read only while the vault holds none; a write of a non-empty value is refused (`refusedSecretPrefs`). */
  assistantApiKey?: string;
  /* Optional proxy URL to forward assistant requests to, instead of calling Anthropic directly. */
  assistantProxyUrl?: string;
  /* Model to use for the AI assistant (defaults to claude-sonnet-4-6 if unset). */
  assistantModel?: string;
  /* Whether the AI assistant is enabled for this org. Defaults to true when a key is configured. */
  assistantEnabled?: boolean;
}

export interface DocPrefs {
  // Notification configuration; used in the enterprise version.
  notifications?: object;
}

/**
 * Combination of DocPrefs for the document and for the current user. This is mostly intended to
 * be the same type, with docDefaults serving as the defaults, and currentUser override them. For
 * prefs that can't be overridden by a user, DocumentOptions may be more suitable.
 */
export interface FullDocPrefs {
  docDefaults: DocPrefs;
  currentUser: DocPrefs;
}

/**
 * List of all deprecated warnings that user can see and dismiss.
 * All of them are marked as seen for new users in FlexServer.ts (welcomeNewUser handler).
 * For now we use then to mark which keyboard shortcuts are deprecated, so those keys
 * are also used in commandList.js.
 *
 * Source code for this feature was deprecated itself :). Here is a link to the latest revision:
 * https://github.com/gristlabs/grist-core/blob/ec20e7fb68786e10979f238c16c432c50a9a7464/app/client/components/DeprecatedCommands.ts
 */
export const DeprecationWarning = StringUnion(
  // Those are not checked anymore. They are kept here for historical reasons (as some users have them marked as seen
  // so they should not be reused).
  // 'deprecatedInsertRowBefore',
  // 'deprecatedInsertRecordAfter',
  // 'deprecatedDeleteRecords',
);
export type DeprecationWarning = typeof DeprecationWarning.type;

export const BehavioralPrompt = StringUnion(
  "referenceColumns",
  "referenceColumnsConfig",
  "rawDataPage",
  "filterButtons",
  "nestedFiltering",
  "pageWidgetPicker",
  "pageWidgetPickerSelectBy",
  "editCardLayout",
  "addNew",
  "rickRow",
  "calendarConfig",

  // The following were used in the past and should not be re-used.
  // 'customURL',
  // 'formsAreHere',
  // 'newAssistant',
  // 'comments',
  // 'accessRules',

);
export type BehavioralPrompt = typeof BehavioralPrompt.type;

export interface BehavioralPromptPrefs {
  /** Defaults to false. */
  dontShowTips: boolean;
  /** List of tips that have been dismissed. */
  dismissedTips: BehavioralPrompt[];
}

/**
 * List of all popups that user can see and dismiss
 */
export const DismissedPopup = StringUnion(
  "deleteRecords",        // confirmation for deleting records keyboard shortcut
  "deleteFields",         // confirmation for deleting columns keyboard shortcut
  "formulaHelpInfo",      // formula help info shown in the popup editor
  "formulaAssistantInfo", // formula assistant info shown in the popup editor
  "supportGrist",         // nudge to opt in to telemetry
  "publishForm",          // confirmation for publishing a form
  "unpublishForm",        // confirmation for unpublishing a form
  "upgradeNewAssistant",  // nudge to upgrade to enterprise shown in the formula assistant

  /* Deprecated */
  "onboardingCards",      // onboarding cards shown on the doc menu
  "tutorialFirstCard",    // first card of the tutorial
);
export type DismissedPopup = typeof DismissedPopup.type;

export const WelcomePopup = StringUnion(
  "coachingCall",
);
export type WelcomePopup = typeof WelcomePopup.type;

export interface DismissedReminder {
  /** The name of the popup. */
  id: WelcomePopup;
  /** Unix timestamp in ms when the popup was last dismissed. */
  lastDismissedAt: number;
  /** If non-null, Unix timestamp in ms when the popup will reappear. */
  nextAppearanceAt: number | null;
  /**  The number of times this popup has been dismissed. */
  timesDismissed: number;
}
